import { Box, Checkbox, FormControlLabel } from '@material-ui/core'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import * as Sentry from '@sentry/react'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button, CircularLoading } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import {
  createPurchaseEvent,
  editUserAttribute,
  publishChangesToPTInventory,
  updatePurchaseEvent,
} from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useStockStore } from 'lib/hooks/useStockStore'
import { useTaxationStore } from 'lib/hooks/useTaxationStore'
import { useTransactionsPolling } from 'lib/hooks/useTransactionsPolling'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { getArticlesMisreadAsUsEdition } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DATA_PREPARATION_ERROR,
  INVALID_PRICE,
  InventoryArticle,
  INVENTORY_TRANSACTION_CAUSES,
  isSingle,
  PublishChangesResponse,
  PurchaseEventPayload,
  roundTo2Decimals,
  SellerPayload,
  STOCK_TYPES,
  toSlimInventoryArticleList,
} from 'shared'
import { SetState } from 'types'
import { ListingTabsState } from 'utils/constants'
import { shallow } from 'zustand/shallow'

interface SaveToInventoryDialogProps {
  articles: InventoryArticle[]
  onArticlesPublished: () => void
  open: boolean
  setOpen: SetState<boolean>
  tabsState: ListingTabsState
}

export const SaveToInventoryDialog = ({
  open,
  articles,
  onArticlesPublished,
  setOpen,
  tabsState,
}: SaveToInventoryDialogProps): JSX.Element => {
  const { user, setUser } = useUser()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { handle } = useErrorHandler()
  const { setShowSpinnerLoading } = useTransactionsSpinnerStore()
  const { fetchUnprocessedTransactions } = useTransactionsPolling()
  const [activeStockType] = useStockStore((s) => [s.activeStockType], shallow)

  const hasArticlesWithoutLot = articles.some((item) => item.purchaseEventId === undefined)
  const articlesMisreadAsUsEdition = getArticlesMisreadAsUsEdition(articles)
  const mustAskLinkToLot =
    user?.enabledNotifications?.askLinkToPurchaseEvent && hasArticlesWithoutLot

  const [confirmWithoutLotChecked, setConfirmWithoutLotChecked] = useState(false)
  const [dontAskAgainChecked, setDontAskAgainChecked] = useState(false)
  // Tracks a completed publish so the success view persists even if the `articles` prop is
  // repopulated by the parent (e.g. when closing the active tab switches activeTab to one
  // with its own selected articles).
  const [publishedSuccessfully, setPublishedSuccessfully] = useState(false)

  const purchaseEvents = useTaxationStore((s) => s.purchaseEvents)

  const activeListingTabId = tabsState.activeTabId
  const activeTab = tabsState.tabs.find((tab) => tab.id === activeListingTabId)
  const lotId = activeTab?.lotId
  const activeLot = lotId !== undefined ? purchaseEvents.find((p) => p.id === lotId) : undefined

  const marginArticlesInVatLot =
    activeLot?.withVat && articles.filter((a) => (a.marginTaxationQuantity ?? 0) > 0)

  useEffect(() => {
    // Every time I reopen the dialog or change the item list, I reset the statuses
    setConfirmWithoutLotChecked(false)
    setDontAskAgainChecked(false)
  }, [open, articles])

  useEffect(() => {
    if (!open) setPublishedSuccessfully(false)
  }, [open])

  if (!user) return <></>

  const handleClose = (): void => {
    setOpen(false)
  }

  const handleToggleConfirmWithoutLot = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmWithoutLotChecked(event.target.checked)
  }

  const handleToggleDontAskAgain = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setDontAskAgainChecked(event.target.checked)
  }

  const handlePublish = async (): Promise<void> => {
    setLoading(true)
    if (activeStockType === STOCK_TYPES.SEALED) {
      articles.map((item) => {
        if (!isSingle(item.card)) {
          item.condition = 'NM'
        }

        return item
      })
    }
    try {
      let publishChangesResponse: PublishChangesResponse
      // if the lot already exists, just send the items to salvage
      if (lotId !== undefined) {
        publishChangesResponse = await publishChangesToPTInventory(
          user,
          toSlimInventoryArticleList(articles),
          true,
          INVENTORY_TRANSACTION_CAUSES.MANUAL_CHANGE
        )
      } else {
        // I create a fake lot to associate them with

        const listingDate = new Date()

        const seller: SellerPayload = {
          addressLine1: '',
          city: '',
          country: '',
          name: '',
          province: '',
          vatNumber: '',
          zipCode: '',
        }

        const purchaseEvent: PurchaseEventPayload = {
          currency: user.currency,
          lotNumber: `Unassigned-${listingDate.getTime()}`,
          price: roundTo2Decimals(
            articles.reduce((acc, art) => acc + (art.buyPrice ?? 0) * art.quantity, 0)
          ),
          quantity: articles.reduce((acc, art) => acc + art.quantity, 0),
          purchaseDate: listingDate,
          withVat: false,
          seller: seller,
        }

        const created = await createPurchaseEvent(purchaseEvent)

        if (created) {
          articles.forEach((article) => {
            article.purchaseEventId = Number(created.id)
          })

          publishChangesResponse = await publishChangesToPTInventory(
            user,
            toSlimInventoryArticleList(articles),
            true,
            INVENTORY_TRANSACTION_CAUSES.MANUAL_CHANGE
          )
        }
      }
      if (publishChangesResponse!.status === 200) {
        if (activeLot) {
          await updatePurchaseEvent(activeLot)
        }

        const resp = await editUserAttribute(
          user.articlesPublishedToday + articles.length,
          'articlesPublishedToday'
        )
        setUser({ ...user, ...resp })

        if (dontAskAgainChecked) {
          const updatedUser = await editUserAttribute(
            {
              ...(user.enabledNotifications ?? {}),
              askLinkToPurchaseEvent: false,
            },
            'enabledNotifications'
          )
          setUser({ ...user, ...resp, ...updatedUser })
        }

        setPublishedSuccessfully(true)
        onArticlesPublished()

        await fetchUnprocessedTransactions()
        setShowSpinnerLoading(true)
      }
    } catch (err: any) {
      console.log(err)
      Sentry.withScope((scope) => {
        scope.setTag('component', 'SaveToInventoryDialog')
        scope.setExtra('context', 'publishChangesToPTInventory')
        scope.setExtra('articles', toSlimInventoryArticleList(articles))
        Sentry.captureException(err)
      })

      const status = err?.response?.status
      const backendMessage = err?.response?.data?.message

      if (status === 422) {
        if (backendMessage === INVALID_PRICE) {
          enqueueSnackbar(`${t('error.invalidPrice')}`, { variant: 'error' })
        } else if (backendMessage === DATA_PREPARATION_ERROR) {
          enqueueSnackbar(`${t('error.cannotSaveArticles')}`, { variant: 'error' })
        }
      } else if (status === 504) {
        enqueueSnackbar(`${t('error.gatewayTimeoutInventoryError')}`, { variant: 'error' })
      } else {
        handle(err)
      }

      console.error('Error during publish changes', JSON.stringify(err))
    }

    setLoading(false)
  }

  const publishDisabledForLimit =
    !!user.subscription.dailyArticlePublishLimit &&
    user.articlesPublishedToday + articles.length > user.subscription.dailyArticlePublishLimit

  /**
   * The button must be disabled if:
   * - I need to ask for confirmation for lots
   * - the checkbox is not yet checked
   * - the user has chosen to save with manual price but has not set the buy price
   */
  const publishDisabled =
    loading || publishDisabledForLimit || (mustAskLinkToLot && !confirmWithoutLotChecked)

  return (
    <ClosableDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      hideButton={true}
      maxWidth="md"
    >
      <DialogTitle disableTypography id="form-dialog-title">
        <h3>{t('ptInventory.addArticles.save')}</h3>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            height="200px"
            width="100%"
            display="flex"
            justifyContent={'center'}
            textAlign={'center'}
          >
            <CircularLoading />
          </Box>
        ) : (
          <>
            {publishedSuccessfully ? (
              <Box>{t('ptInventory.addArticles.success.text')}</Box>
            ) : (
              <Box>
                <p className="fs-6 fw-semibold">
                  {t('ptInventory.addArticles.confirmAdd', {
                    count: articles.length,
                  })}
                </p>

                {articlesMisreadAsUsEdition.length > 0 && (
                  <div
                    className="alert alert-warning mt-3 mb-0 p-2"
                    style={{ fontSize: '0.85rem' }}
                    data-testid="us-edition-comment-warning"
                  >
                    <div className="d-flex flex-row align-items-center">
                      <i className="fa-light fa-triangle-exclamation me-2 fs-6" />
                      <span>
                        {t('card.usEditionCommentBeforeSaving', {
                          count: articlesMisreadAsUsEdition.length,
                        })}
                      </span>
                    </div>
                  </div>
                )}

                {user.featureFlags?.taxation &&
                  marginArticlesInVatLot &&
                  marginArticlesInVatLot.length > 0 && (
                    <div
                      className="alert alert-warning mt-3 mb-0 p-2"
                      style={{ fontSize: '0.85rem' }}
                    >
                      <div className="d-flex flex-row align-items-center">
                        <i className="fa-light fa-triangle-exclamation me-2 fs-6" />
                        <span>
                          {t('taxation.marginArticlesInVatLotWarning', {
                            count: marginArticlesInVatLot.length,
                          })}
                        </span>
                      </div>
                    </div>
                  )}
              </Box>
            )}

            {/* "Save without batch" confirmation checkbox */}
            {user.featureFlags?.taxation && mustAskLinkToLot && !publishedSuccessfully && (
              <Box marginTop={3} height={100} display={'flex'} flexDirection={'column'}>
                <FormControlLabel
                  control={
                    <Checkbox
                      data-testid="publish-changes-checkbox"
                      checked={confirmWithoutLotChecked}
                      onChange={handleToggleConfirmWithoutLot}
                      color="primary"
                    />
                  }
                  label={t('publish.saveWithoutPe')}
                />

                {/* Second checkbox: Don't ask me again */}
                {confirmWithoutLotChecked && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dontAskAgainChecked}
                        onChange={handleToggleDontAskAgain}
                        color="primary"
                      />
                    }
                    label={t('publish.dontAskReminder')}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Box height="1rem" />

      <DialogActions>
        {!loading && (
          <div className="d-flex flex-row w-100 justify-content-around">
            <Button
              variant="outlined"
              onClick={handleClose}
              color="primary"
              disabled={loading}
              data-testid="close-btn"
            >
              {t('common.close')}
            </Button>
            {articles.length > 0 && !publishedSuccessfully && (
              <Button
                data-testid="start-publish-btn"
                variant="contained"
                onClick={handlePublish}
                tooltip={
                  user.subscription.dailyArticlePublishLimit &&
                  user.articlesPublishedToday + articles.length >
                    user.subscription.dailyArticlePublishLimit
                    ? t('addArticles.articlesPublishedOverLimit', {
                        count:
                          user.subscription.dailyArticlePublishLimit - user.articlesPublishedToday,
                      })
                    : ''
                }
                disabled={publishDisabled}
                color="primary"
              >
                {loading ? t('publish.publishing') : t('common.save')}
              </Button>
            )}
          </div>
        )}
      </DialogActions>
    </ClosableDialog>
  )
}
